/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-width is an internal custom property from #utils/skeleton.css, not a design token */
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { styleMap } from 'lit/directives/style-map.js'
import { property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import styles, { boolean } from '../form-boolean/lfds-boolean.css.ts'
import { LfuiElement } from '#utils/element'
import { WithFocus, compose } from '#utils/mixins'
import skeletonStyles from '#utils/skeleton.css'
import { randomSkeletonCharWidth, skeletonSrLabel } from '#utils/skeleton'

// Roughly the longest label we'd expect a radio to have; the shimmer bar is
// a random percentage cut of this (see randomSkeletonCharWidth).
const MAX_LABEL_CHARS = 24

/**
 * @name LfdsRadio
 * @tag lfds-radio
 * @status ready
 *
 * @description
 * Individual radio button. Always use inside `lfds-radio-group`, which owns
 * form participation and keyboard navigation. Mutual exclusion among radios
 * with the same `name` is handled via the static `groups` map.
 *
 * @slot label - Override the label content.
 *
 * @cssproperty --lfds--form-boolean-state-opacity - Opacity of the control area's state-layer background. Set by parent components (e.g. list-row) to propagate hover state.
 */
export default class LfdsRadio extends compose(LfuiElement, WithFocus) {
	static styles = [LfuiElement.styles, skeletonStyles, styles, boolean]
	static events = ['change']

	/** Tracks all connected radios keyed by name, for mutual exclusion. */
	static groups: Map<string, Set<LfdsRadio>> = new Map()

	protected delegatedEvents = ['change', 'focus', 'blur', 'focusin', 'focusout']

	@property() label!: string
	@property() name: string
	@property() value: string
	@property() autocomplete: 'on' | 'off'
	@property({ type: Boolean }) checked: boolean = false
	@property({ type: Boolean }) required: boolean
	@property({ type: Boolean }) disabled: boolean
	@property({ type: Boolean }) invalid: boolean

	/**
	 * Shows a shimmering placeholder over the control box and label text
	 * instead of the real content, for a value that isn't available yet.
	 */
	@property({ type: Boolean, reflect: true }) loading: boolean

	/**
	 * Overrides the generic loading screen-reader description. Pass a
	 * contextual string when this radio's content is known, or `''` to
	 * suppress the description entirely when a wrapping region already
	 * announces loading for several grouped fields. Unset keeps the shared
	 * generic wording.
	 */
	@property({ attribute: 'sr-loading-label' }) srLoadingLabel?: string

	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string

	#input = createRef<HTMLInputElement>()
	#labels = refCollection()
	#descriptions = refCollection()
	#labelWidth = randomSkeletonCharWidth(MAX_LABEL_CHARS)

	connectedCallback() {
		super.connectedCallback()
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')
		this.#addToGroup()
		this.addEventListener('click', this.#handleHostClick)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('click', this.#handleHostClick)
		LfdsRadio.groups.get(this.name)?.delete(this)
		if (LfdsRadio.groups.get(this.name)?.size === 0) {
			LfdsRadio.groups.delete(this.name)
		}
	}

	updated(changed) {
		super.updated(changed)
		if (changed.has('loading')) {
			this.setAttribute('aria-busy', String(!!this.loading))
		}
		if (changed.has('name')) {
			const oldName = changed.get('name') as string
			if (oldName) {
				LfdsRadio.groups.get(oldName)?.delete(this)
				if (LfdsRadio.groups.get(oldName)?.size === 0) {
					LfdsRadio.groups.delete(oldName)
				}
			}
			this.#addToGroup()
		}
	}

	#addToGroup() {
		if (this.name) {
			if (!LfdsRadio.groups.has(this.name)) {
				LfdsRadio.groups.set(this.name, new Set())
			}
			LfdsRadio.groups.get(this.name)!.add(this)
		}
	}

	#handleHostClick = (event: Event) => {
		if (event.composedPath()[0] === this) {
			this.#input.value?.click()
		}
	}

	#handleChange = (_event: Event) => {
		if (this.name && !LfdsRadio.groups.get(this.name)?.has(this)) {
			this.#addToGroup()
		}

		// Uncheck all siblings in the same group
		for (const radio of LfdsRadio.groups.get(this.name) || []) {
			if (radio !== this) radio.checked = false
		}

		this.checked = true
		this.invalid = false
	}

	render() {
		return html`
			<label
				class=${classMap({
					wrapper: true,
					'focus-visible': this.focusVisible
				})}
			>
				<div
					class=${classMap({
						control: true,
						checked: this.checked,
						invalid: this.invalid,
						loading: this.loading
					})}
				>
					<input
						id="input"
						type="radio"
						class="boolean radio"
						aria-invalid=${this.invalid ? 'true' : (nothing as any)}
						aria-label=${ifDefined(this.srLabel)}
						value=${ifDefined(this.value)}
						name=${ifDefined(this.name)}
						autocomplete=${ifDefined(this.autocomplete)}
						?required=${this.required}
						?disabled=${this.disabled || this.loading}
						.checked=${this.checked}
						${ref(this.#input)}
						${delegateEvents(this.delegatedEvents, {
							before: { change: this.#handleChange }
						})}
						${this.#labels.bind('ariaLabelledByElements')}
						${this.#descriptions.bind('ariaDescribedByElements')}
						${ariaElements({
							describedBy: this.srDescribedby,
							labelledBy: this.srLabelledby,
							controls: this.srControls
						})}
					/>
					<span class="radio-dot"></span>
				</div>
				${
					this.label || this.querySelector('[slot="label"]')
						? html`<span
								class=${classMap({ label: true, loading: this.loading })}
								style=${styleMap({
									'--lfds-skeleton-width': `${this.#labelWidth}ch`
								})}
								${this.#labels.add()}
							>
								<slot name="label">${this.label}</slot>
							</span>`
						: nothing
				}
			</label>
			${
				this.loading && skeletonSrLabel(this, this.srLoadingLabel)
					? html`<span class="hidden" role="status" ${this.#descriptions.add()}>
							${skeletonSrLabel(this, this.srLoadingLabel)}
						</span>`
					: nothing
			}
		`
	}
}
